import {useCallback, useEffect, useMemo, useState} from "react";
import Autocomplete from "react-google-autocomplete";
import {isAuthenticated} from "../apicalls/auth";
import {
  assignVendorStaff,
  deleteRestringingVendor,
  deleteRestringingRecommendationCategory,
  getRestringingVendor,
  getRestringingVendorImageUpload,
  getRestringingOrder,
  getRestringingOrders,
  getRestringingRecommendationCategories,
  getRestringingServiceTiers,
  getRestringingStrings,
  getRestringingVendors,
  signupVendorStaff,
  updateRestringingVendor,
  updateRestringingRecommendationCategory,
  updateRestringingServiceTier,
  updateRestringingString,
  upsertRestringingServiceTier,
  upsertRestringingVendor,
  upsertRestringingRecommendationCategory,
  upsertRestringingString,
} from "../apicalls/restringingVendors";
import {googleApiKey} from "../../constAPI";
import "./style/RestringingVendors.css";

const initialForm = {
  vendorId: "",
  email: "",
  password: "",
  role: "owner",
};

const vendorHourDays = [
  {key: "mon", label: "Monday"},
  {key: "tue", label: "Tuesday"},
  {key: "wed", label: "Wednesday"},
  {key: "thu", label: "Thursday"},
  {key: "fri", label: "Friday"},
  {key: "sat", label: "Saturday"},
  {key: "sun", label: "Sunday"},
];

export function createInitialHoursRows() {
  return vendorHourDays.reduce((rows, day) => ({
    ...rows,
    [day.key]: {
      closed: true,
      open: "09:00",
      close: "17:00",
    },
  }), {});
}

function normalizeTime(value) {
  const [hours = "", minutes = ""] = String(value || "").split(":");
  const hourNumber = Number(hours);
  const minuteNumber = Number(minutes);
  if (!Number.isInteger(hourNumber) || !Number.isInteger(minuteNumber)) return "";
  if (hourNumber < 0 || hourNumber > 23 || minuteNumber < 0 || minuteNumber > 59) return "";
  return `${String(hourNumber).padStart(2, "0")}:${String(minuteNumber).padStart(2, "0")}`;
}

export function parseVendorHours(hoursValue) {
  const rows = createInitialHoursRows();
  if (!hoursValue) return rows;

  let hours = hoursValue;
  if (typeof hoursValue === "string") {
    if (!hoursValue.trim()) return rows;
    try {
      hours = JSON.parse(hoursValue);
    } catch (error) {
      return rows;
    }
  }
  if (!hours || typeof hours !== "object") return rows;

  vendorHourDays.forEach((day) => {
    const range = hours[day.key];
    if (typeof range !== "string" || !range.includes("-")) return;
    const [openValue, closeValue] = range.split("-");
    const open = normalizeTime(openValue.trim());
    const close = normalizeTime(closeValue.trim());
    if (!open || !close) return;
    rows[day.key] = {closed: false, open, close};
  });

  return rows;
}

export async function persistUploadedVendorImage({vendorId, imageUrl, token, updateVendor = updateRestringingVendor}) {
  const id = Number(vendorId);
  if (!Number.isInteger(id) || id <= 0) return null;
  return updateVendor(id, {image_url: imageUrl}, token);
}

export function buildVendorHours(rows) {
  return vendorHourDays.reduce((hours, day) => {
    const row = rows?.[day.key];
    if (!row || row.closed || !row.open || !row.close) return hours;
    return {
      ...hours,
      [day.key]: `${row.open}-${row.close}`,
    };
  }, {});
}

function createInitialVendorForm() {
  return {
    name: "",
    owner_name: "",
    contact_email: "",
    cell_phone: "",
    address: "",
    lat: "",
    lng: "",
    description: "",
    image_url: "",
    website_url: "",
    google_business_url: "",
    collection_details: "",
    hoursRows: createInitialHoursRows(),
    turnaround_days: "3",
    tax_rate: "0",
    stripe_account_id: "",
    platform_fee_bps: "0",
    active: true,
  };
}

const initialVendorForm = createInitialVendorForm();

const statusOptions = [
  "pending",
  "dropped_off",
  "in_progress",
  "ready_for_pickup",
  "picked_up",
  "fulfilled",
  "cancelled",
];

const paymentStatusOptions = ["unpaid", "paid", "cancelled", "refunded", "payment_failed"];
const stringCategories = ["syn_gut", "std_multi", "prem_multi", "std_poly", "prem_poly", "poly_multi", "gut_poly", "nat_gut"];
const serviceCompositions = ["poly_multi_hybrid", "natural_gut_hybrid", "natural_gut"];
const stringGaugeOptions = ["15L", "16", "16L", "17", "17L", "18"];

const initialServiceTierForm = {
  name: "",
  price: "",
  string_composition: "",
  string_category: "",
  active: true,
};

const initialStringForm = {
  brand: "",
  name: "",
  category: "syn_gut",
  gauges: [],
  active: true,
};

const initialRecommendationForm = {
  category: "syn_gut",
  category_label: "",
  default_tension_lbs: "",
  tension_min_lbs: "",
  tension_max_lbs: "",
  active: true,
};

function formatStatus(value) {
  return value ? value.replace(/_/g, " ") : "-";
}

export function serviceCompositionLabel(composition) {
  const labels = {
    poly_multi_hybrid: "Polyester + Multifilament hybrid",
    natural_gut_hybrid: "Natural gut hybrid",
    natural_gut: "Natural gut",
  };
  return labels[composition] || "Single string / customer supplied";
}

export function serviceTierPayload({name, price, string_composition, string_category, active}) {
  const payload = {
    name: name.trim(),
    price_cents: Math.round(Number(price) * 100),
    active: Boolean(active),
  };
  if (string_composition) {
    payload.string_composition = string_composition;
    payload.string_category = null;
  } else if (string_category) {
    payload.string_category = string_category;
    payload.string_composition = null;
  }
  return payload;
}

function formatMoney(cents) {
  if (cents === null || cents === undefined) return "-";
  return `$${(Number(cents) / 100).toFixed(2)}`;
}

export function coachOrderLabel(order = {}) {
  if (!order.coach_id) return "-";
  return order.coach_name ? `Coach · ${order.coach_name}` : `Coach #${order.coach_id}`;
}

export function vendorNetCents(order = {}) {
  if (order.vendor_net_cents !== null && order.vendor_net_cents !== undefined) {
    return Number(order.vendor_net_cents);
  }
  return Number(order.total_cents || 0) - Number(order.coach_commission_cents || 0);
}

function formatDate(value) {
  if (!value) return "-";
  return new Date(value).toLocaleString();
}

function formatString(item) {
  if (item.string_selection === "shop_choice") return "Shop's choice";
  if (item.string_selection === "player_supplied") return item.own_string_text || "Player's own string";
  if (item.own_string_text) return item.own_string_text;
  if (item.custom_string_text) return item.custom_string_text;
  if (item.string_brand || item.string_name) {
    return [item.string_brand, item.string_name].filter(Boolean).join(" ");
  }
  return item.master_string_id || item.string_id || "-";
}

function normalizeGauges(value, fallbackGauge = "") {
  const source = Array.isArray(value) ? value : fallbackGauge ? [fallbackGauge] : [];
  return [...new Set(source.map((gauge) => String(gauge || "").trim()).filter(Boolean))];
}

export function latestPaymentLink(orderDetail) {
  if (orderDetail?.paymentLink?.url) return orderDetail.paymentLink;
  if (orderDetail?.payment_link?.url) return orderDetail.payment_link;
  if (orderDetail?.order?.paymentLink?.url) return orderDetail.order.paymentLink;

  const links = orderDetail?.payment_links || [];
  return links.find((link) => link.url && !link.paid_at) || links.find((link) => link.url) || links[0] || null;
}

export function canCopyPaymentLink(orderDetail) {
  const link = latestPaymentLink(orderDetail);
  if (!link?.url) return false;
  const expiresAt = link.expires_at ? new Date(link.expires_at).getTime() : null;
  if (Number.isFinite(expiresAt) && expiresAt <= Date.now()) return false;
  const paymentStatus = orderDetail?.payment_status || orderDetail?.order?.payment_status;
  const fulfillmentStatus = orderDetail?.fulfillment_status || orderDetail?.order?.fulfillment_status;
  return paymentStatus !== "paid" && !["cancelled", "fulfilled", "picked_up"].includes(fulfillmentStatus);
}

export function formatVendorStaff(staff) {
  const rows = Array.isArray(staff) ? staff : [];
  return rows
    .filter((member) => member?.email)
    .map((member) => ({
      email: member.email,
      meta: [member.role || "staff", member.active === false ? "inactive" : "active"].join(" · "),
    }));
}

function nullableNumber(value) {
  if (value === "" || value === null || value === undefined) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

export function vendorFormFromVendor(vendor) {
  return {
    ...createInitialVendorForm(),
    name: vendor.name || "",
    owner_name: vendor.owner_name || "",
    contact_email: vendor.contact_email || "",
    cell_phone: vendor.cell_phone || vendor.public_phone || "",
    address: vendor.address || "",
    lat: vendor.lat === null || vendor.lat === undefined ? "" : String(vendor.lat),
    lng: vendor.lng === null || vendor.lng === undefined ? "" : String(vendor.lng),
    description: vendor.description || "",
    image_url: vendor.image_url || "",
    website_url: vendor.website_url || "",
    google_business_url: vendor.google_business_url || "",
    collection_details: vendor.collection_details || "",
    hoursRows: parseVendorHours(vendor.hours),
    turnaround_days: vendor.turnaround_days === null || vendor.turnaround_days === undefined ? "3" : String(vendor.turnaround_days),
    tax_rate: vendor.tax_rate === null || vendor.tax_rate === undefined ? "0" : String(vendor.tax_rate),
    stripe_account_id: vendor.stripe_account_id || "",
    platform_fee_bps: vendor.platform_fee_bps === null || vendor.platform_fee_bps === undefined ? "0" : String(vendor.platform_fee_bps),
    active: vendor.active !== false,
  };
}

export function restringingVendorImageFileType(file) {
  const fileType = String(file?.type || "").split("/")[1] || "";
  if (fileType === "jpg") return "jpeg";
  return ["jpeg", "png"].includes(fileType) ? fileType : "";
}

function vendorAddressFromPlace(place) {
  const formattedAddress = place?.formatted_address || "";
  return formattedAddress.startsWith(place?.name)
    ? formattedAddress
    : [place?.name, formattedAddress].filter(Boolean).join(" ");
}

function RestringingVendors() {
  const user = isAuthenticated();
  const accessToken = user?.access_token;
  const [vendors, setVendors] = useState([]);
  const [orders, setOrders] = useState([]);
  const [filters, setFilters] = useState({
    vendor_id: "",
    status: "",
    payment_status: "",
  });
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState("");
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [serviceTiers, setServiceTiers] = useState([]);
  const [serviceTierEdits, setServiceTierEdits] = useState({});
  const [serviceTierForm, setServiceTierForm] = useState(initialServiceTierForm);
  const [strings, setStrings] = useState([]);
  const [stringEdits, setStringEdits] = useState({});
  const [stringForm, setStringForm] = useState(initialStringForm);
  const [recommendationCategories, setRecommendationCategories] = useState([]);
  const [recommendationEdits, setRecommendationEdits] = useState({});
  const [recommendationForm, setRecommendationForm] = useState(initialRecommendationForm);
  const [catalogMessage, setCatalogMessage] = useState("");
  const [catalogError, setCatalogError] = useState("");
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [editingVendorId, setEditingVendorId] = useState("");
  const [vendorForm, setVendorForm] = useState(initialVendorForm);
  const [vendorSaving, setVendorSaving] = useState(false);
  const [vendorImageUploading, setVendorImageUploading] = useState(false);
  const [vendorMessage, setVendorMessage] = useState("");
  const [vendorError, setVendorError] = useState("");
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const loadVendors = useCallback(() => {
    return getRestringingVendors(accessToken).then((data) => {
      const list = data?.vendors || [];
      setVendors(list);
      const tennisGarage = list.find((vendor) => vendor.name === "The Tennis Garage");
      const defaultVendor = tennisGarage || list[0];
      if (defaultVendor) {
        setForm((current) => ({
          ...current,
          vendorId: current.vendorId || String(defaultVendor.id),
        }));
      }
    });
  }, [accessToken]);

  const loadCatalog = useCallback(() => {
    setCatalogLoading(true);
    setCatalogError("");
    return Promise.all([
      getRestringingServiceTiers(accessToken),
      getRestringingStrings(accessToken),
      getRestringingRecommendationCategories(accessToken),
    ])
      .then(([tierData, stringData, recommendationData]) => {
        if (tierData?.detail || stringData?.detail || recommendationData?.detail) {
          setCatalogError(tierData?.detail || stringData?.detail || recommendationData?.detail);
          return;
        }
        const tiers = tierData?.service_tiers || [];
        const masterStrings = stringData?.strings || [];
        const recommendationConfigs = recommendationData?.recommendation_categories || [];
        setServiceTiers(tiers);
        setStrings(masterStrings);
        setRecommendationCategories(recommendationConfigs);
        setServiceTierEdits(tiers.reduce((edits, tier) => ({
          ...edits,
          [tier.id]: {
            name: tier.name || "",
            price: tier.price_cents === undefined ? "" : (Number(tier.price_cents) / 100).toFixed(2),
            string_composition: tier.string_composition || "",
            string_category: tier.string_category || "",
            active: tier.active !== false,
          },
        }), {}));
        setStringEdits(masterStrings.reduce((edits, string) => ({
          ...edits,
          [string.id]: {
            brand: string.brand || "",
            name: string.name || "",
            category: string.category || "syn_gut",
            gauges: normalizeGauges(string.gauges, string.gauge),
            active: string.active !== false,
          },
        }), {}));
        setRecommendationEdits(recommendationConfigs.reduce((edits, item) => ({
          ...edits,
          [item.category]: {
            category_label: item.category_label || "",
            default_tension_lbs: item.default_tension_lbs === undefined ? "" : String(Number(item.default_tension_lbs)),
            tension_min_lbs: item.tension_min_lbs === undefined ? "" : String(Number(item.tension_min_lbs)),
            tension_max_lbs: item.tension_max_lbs === undefined ? "" : String(Number(item.tension_max_lbs)),
            active: item.active !== false,
          },
        }), {}));
      })
      .finally(() => setCatalogLoading(false));
  }, [accessToken]);

  useEffect(() => {
    if (!accessToken) return;

    loadCatalog();
  }, [accessToken, loadCatalog]);

  useEffect(() => {
    if (!accessToken) return;

    loadVendors();
  }, [accessToken, loadVendors]);

  useEffect(() => {
    if (!accessToken) return;

    setOrdersLoading(true);
    setOrdersError("");
    getRestringingOrders(accessToken, filters)
      .then((data) => {
        if (data?.detail) {
          setOrdersError(data.detail);
          setOrders([]);
          return;
        }
        setOrders(data?.orders || []);
      })
      .finally(() => setOrdersLoading(false));
  }, [accessToken, filters]);

  const selectedVendor = useMemo(
    () => vendors.find((vendor) => String(vendor.id) === form.vendorId),
    [vendors, form.vendorId]
  );

  const updateFilters = (event) => {
    const {name, value} = event.target;
    setSelectedOrderId("");
    setSelectedOrder(null);
    setFilters((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const updateForm = (event) => {
    const {name, value} = event.target;
    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const updateVendorForm = (event) => {
    const {name, type, checked, value} = event.target;
    setVendorForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const updateVendorHours = (dayKey, field, value) => {
    setVendorForm((current) => ({
      ...current,
      hoursRows: {
        ...current.hoursRows,
        [dayKey]: {
          ...current.hoursRows[dayKey],
          [field]: value,
        },
      },
    }));
  };

  const resetVendorForm = () => {
    setEditingVendorId("");
    setVendorForm(createInitialVendorForm());
    setVendorError("");
    setVendorMessage("");
  };

  const buildVendorPayload = () => {
    if (!vendorForm.name.trim() || !vendorForm.address.trim() || !vendorForm.cell_phone.trim()) {
      return {error: "Store name, location, and cell are required."};
    }

    const vendorHours = buildVendorHours(vendorForm.hoursRows);
    const hours = Object.keys(vendorHours).length ? vendorHours : null;

    const turnaroundDays = Number(vendorForm.turnaround_days);
    const taxRate = Number(vendorForm.tax_rate);
    const platformFeeBps = Number(vendorForm.platform_fee_bps);
    if (!Number.isInteger(turnaroundDays) || turnaroundDays < 0) return {error: "Turnaround days must be 0 or greater."};
    if (!Number.isFinite(taxRate) || taxRate < 0) return {error: "Tax rate must be 0 or greater."};
    if (!Number.isInteger(platformFeeBps) || platformFeeBps < 0) return {error: "Platform fee bps must be 0 or greater."};

    return {
      payload: {
        name: vendorForm.name.trim(),
        address: vendorForm.address.trim(),
        lat: nullableNumber(vendorForm.lat),
        lng: nullableNumber(vendorForm.lng),
        public_phone: vendorForm.cell_phone.trim(),
        owner_name: vendorForm.owner_name.trim() || null,
        contact_email: vendorForm.contact_email.trim() || null,
        cell_phone: vendorForm.cell_phone.trim(),
        description: vendorForm.description.trim() || null,
        image_url: vendorForm.image_url.trim() || null,
        website_url: vendorForm.website_url.trim() || null,
        google_business_url: vendorForm.google_business_url.trim() || null,
        collection_details: vendorForm.collection_details.trim() || null,
        hours,
        turnaround_days: turnaroundDays,
        tax_rate: taxRate,
        stripe_account_id: vendorForm.stripe_account_id.trim() || null,
        platform_fee_bps: platformFeeBps,
        active: Boolean(vendorForm.active),
      },
    };
  };

  const editVendor = async (vendorId) => {
    setVendorError("");
    setVendorMessage("");
    const response = await getRestringingVendor(vendorId, accessToken);
    if (response?.detail || !response?.vendor) {
      setVendorError(response?.detail || "Vendor fetch failed.");
      return;
    }
    setEditingVendorId(String(vendorId));
    setVendorForm(vendorFormFromVendor(response.vendor));
  };

  const saveVendor = async (event) => {
    event.preventDefault();
    setVendorError("");
    setVendorMessage("");
    const {payload, error: validationError} = buildVendorPayload();
    if (validationError) {
      setVendorError(validationError);
      return;
    }

    setVendorSaving(true);
    try {
      const response = editingVendorId
        ? await updateRestringingVendor(editingVendorId, payload, accessToken)
        : await upsertRestringingVendor(payload, accessToken);
      if (response?.detail || !response?.vendor) {
        setVendorError(response?.detail || "Vendor save failed.");
        return;
      }
      setVendorMessage(editingVendorId ? "Vendor saved." : "Vendor created.");
      setEditingVendorId("");
      setVendorForm(createInitialVendorForm());
      await loadVendors();
    } finally {
      setVendorSaving(false);
    }
  };

  const uploadVendorImage = async (event) => {
    const imageFile = event.target.files?.[0];
    if (!imageFile) return;

    const fileType = restringingVendorImageFileType(imageFile);
    setVendorError("");
    setVendorMessage("");
    if (!fileType) {
      setVendorError("Upload a JPEG or PNG image.");
      event.target.value = "";
      return;
    }

    setVendorImageUploading(true);
    try {
      const upload = await getRestringingVendorImageUpload({
        vendorId: editingVendorId,
        fileType,
        token: accessToken,
      });
      if (upload?.detail || !upload?.uploadURL) {
        setVendorError(upload?.detail || "Image upload failed.");
        return;
      }

      const uploadResponse = await fetch(upload.uploadURL, {
        method: "PUT",
        headers: {"Content-Type": imageFile.type},
        body: imageFile,
      });
      if (!uploadResponse.ok) {
        setVendorError("Image upload failed.");
        return;
      }

      const imageUrl = upload.url || upload.Key || "";
      const persistedImage = await persistUploadedVendorImage({
        vendorId: editingVendorId,
        imageUrl,
        token: accessToken,
      });
      if (persistedImage && (persistedImage?.detail || !persistedImage?.vendor)) {
        setVendorError(persistedImage?.detail || "Image uploaded but could not be saved to the vendor.");
        return;
      }

      setVendorForm((current) => ({
        ...current,
        image_url: imageUrl || current.image_url,
      }));
      setVendorMessage(editingVendorId ? "Image uploaded and saved." : "Image uploaded. Save the vendor to keep it.");
    } catch (error) {
      setVendorError("Image upload failed.");
    } finally {
      setVendorImageUploading(false);
      event.target.value = "";
    }
  };

  const deactivateVendor = async (vendorId) => {
    if (!window.confirm("Deactivate this vendor? Existing orders stay preserved.")) return;
    setVendorError("");
    setVendorMessage("");
    const response = await deleteRestringingVendor(vendorId, accessToken);
    if (response?.detail || !response?.vendor) {
      setVendorError(response?.detail || "Vendor deactivation failed.");
      return;
    }
    setVendorMessage("Vendor deactivated.");
    await loadVendors();
  };

  const updateTierEdit = (tierId, field, value) => {
    setServiceTierEdits((current) => ({
      ...current,
      [tierId]: {
        ...(current[tierId] || {}),
        [field]: value,
      },
    }));
  };

  const updateServiceTierForm = (event) => {
    const {name, type, checked, value} = event.target;
    setServiceTierForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const updateStringEdit = (stringId, field, value) => {
    setStringEdits((current) => ({
      ...current,
      [stringId]: {
        ...(current[stringId] || {}),
        [field]: value,
      },
    }));
  };

  const updateStringForm = (event) => {
    const {name, type, checked, value} = event.target;
    setStringForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const toggleStringFormGauge = (gauge) => {
    setStringForm((current) => {
      const gauges = normalizeGauges(current.gauges);
      return {
        ...current,
        gauges: gauges.includes(gauge)
          ? gauges.filter((item) => item !== gauge)
          : [...gauges, gauge],
      };
    });
  };

  const toggleStringEditGauge = (stringId, gauge) => {
    setStringEdits((current) => {
      const edit = current[stringId] || {};
      const gauges = normalizeGauges(edit.gauges);
      return {
        ...current,
        [stringId]: {
          ...edit,
          gauges: gauges.includes(gauge)
            ? gauges.filter((item) => item !== gauge)
            : [...gauges, gauge],
        },
      };
    });
  };

  const updateRecommendationEdit = (category, field, value) => {
    setRecommendationEdits((current) => ({
      ...current,
      [category]: {
        ...(current[category] || {}),
        [field]: value,
      },
    }));
  };

  const updateRecommendationForm = (event) => {
    const {name, type, checked, value} = event.target;
    setRecommendationForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const saveTier = async (tierId) => {
    const edit = serviceTierEdits[tierId];
    setCatalogError("");
    setCatalogMessage("");
    if (!edit?.name || !edit?.price) {
      setCatalogError("Service name and price are required.");
      return;
    }

    const response = await updateRestringingServiceTier(tierId, serviceTierPayload(edit), accessToken);
    if (response?.detail) {
      setCatalogError(response.detail);
      return;
    }
    setCatalogMessage("Service tier saved.");
    await loadCatalog();
  };

  const createServiceTier = async (event) => {
    event.preventDefault();
    setCatalogError("");
    setCatalogMessage("");
    if (!serviceTierForm.name || !serviceTierForm.price) {
      setCatalogError("Service name and price are required.");
      return;
    }

    const response = await upsertRestringingServiceTier(serviceTierPayload(serviceTierForm), accessToken);
    if (response?.detail) {
      setCatalogError(response.detail);
      return;
    }
    setServiceTierForm(initialServiceTierForm);
    setCatalogMessage("Service tier created.");
    await loadCatalog();
  };

  const saveString = async (stringId) => {
    const edit = stringEdits[stringId];
    setCatalogError("");
    setCatalogMessage("");
    if (!edit?.brand || !edit?.name || !edit?.category) {
      setCatalogError("String brand, name, and category are required.");
      return;
    }

    const response = await updateRestringingString(stringId, {
      brand: edit.brand.trim(),
      name: edit.name.trim(),
      category: edit.category,
      gauges: normalizeGauges(edit.gauges),
      active: Boolean(edit.active),
    }, accessToken);
    if (response?.detail) {
      setCatalogError(response.detail);
      return;
    }
    setCatalogMessage("String saved.");
    await loadCatalog();
  };

  const createString = async (event) => {
    event.preventDefault();
    setCatalogError("");
    setCatalogMessage("");
    if (!stringForm.brand || !stringForm.name || !stringForm.category) {
      setCatalogError("String brand, name, and category are required.");
      return;
    }

    const response = await upsertRestringingString({
      brand: stringForm.brand.trim(),
      name: stringForm.name.trim(),
      category: stringForm.category,
      gauges: normalizeGauges(stringForm.gauges),
      active: Boolean(stringForm.active),
    }, accessToken);
    if (response?.detail) {
      setCatalogError(response.detail);
      return;
    }
    setCatalogMessage("String option saved.");
    setStringForm(initialStringForm);
    await loadCatalog();
  };

  const recommendationPayload = (source) => ({
    category_label: source.category_label.trim(),
    default_tension_lbs: Number(source.default_tension_lbs),
    tension_min_lbs: Number(source.tension_min_lbs),
    tension_max_lbs: Number(source.tension_max_lbs),
    active: Boolean(source.active),
  });

  const validateRecommendation = (source) => {
    if (!source?.category_label || !source?.default_tension_lbs || !source?.tension_min_lbs || !source?.tension_max_lbs) {
      return "Label and all tension fields are required.";
    }
    const min = Number(source.tension_min_lbs);
    const max = Number(source.tension_max_lbs);
    const defaultTension = Number(source.default_tension_lbs);
    if (![min, max, defaultTension].every(Number.isFinite) || min > max || defaultTension < min || defaultTension > max) {
      return "Tension min must be <= default <= max.";
    }
    return "";
  };

  const createRecommendation = async (event) => {
    event.preventDefault();
    setCatalogError("");
    setCatalogMessage("");
    const validation = validateRecommendation(recommendationForm);
    if (validation) {
      setCatalogError(validation);
      return;
    }

    const response = await upsertRestringingRecommendationCategory({
      category: recommendationForm.category,
      ...recommendationPayload(recommendationForm),
    }, accessToken);
    if (response?.detail) {
      setCatalogError(response.detail);
      return;
    }
    setCatalogMessage("Recommendation category saved.");
    setRecommendationForm(initialRecommendationForm);
    await loadCatalog();
  };

  const saveRecommendation = async (category) => {
    const edit = recommendationEdits[category];
    setCatalogError("");
    setCatalogMessage("");
    const validation = validateRecommendation(edit);
    if (validation) {
      setCatalogError(validation);
      return;
    }

    const response = await updateRestringingRecommendationCategory(category, recommendationPayload(edit), accessToken);
    if (response?.detail) {
      setCatalogError(response.detail);
      return;
    }
    setCatalogMessage("Recommendation category saved.");
    await loadCatalog();
  };

  const disableRecommendation = async (category) => {
    setCatalogError("");
    setCatalogMessage("");
    const response = await deleteRestringingRecommendationCategory(category, accessToken);
    if (response?.detail) {
      setCatalogError(response.detail);
      return;
    }
    setCatalogMessage("Recommendation category disabled.");
    await loadCatalog();
  };

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    setMessage("");

    if (!form.vendorId || !form.email || !form.password || !form.role) {
      setError("Vendor, email, password, and role are required.");
      return;
    }

    setLoading(true);
    try {
      const signup = await signupVendorStaff({
        email: form.email.trim(),
        password: form.password,
      });

      if (!signup?.user_id) {
        setError(signup?.detail || "Vendor user creation failed.");
        return;
      }

      const assignment = await assignVendorStaff({
        vendorId: form.vendorId,
        userId: signup.user_id,
        role: form.role.trim(),
        token: accessToken,
      });

      if (!assignment?.staff) {
        setError(assignment?.detail || "Vendor staff assignment failed.");
        return;
      }

      setMessage(`Vendor user #${signup.user_id} mapped to ${selectedVendor?.name || "vendor"}.`);
      await loadVendors();
      setForm((current) => ({
        ...initialForm,
        vendorId: current.vendorId,
      }));
    } finally {
      setLoading(false);
    }
  };

  const viewOrder = async (orderId) => {
    setSelectedOrderId(orderId);
    setSelectedOrder(null);
    setDetailLoading(true);
    try {
      const data = await getRestringingOrder(orderId, accessToken);
      setSelectedOrder(data);
    } finally {
      setDetailLoading(false);
    }
  };

  const copyPaymentLink = async (link) => {
    if (!link?.url) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setOrdersError("");
    } catch (error) {
      setOrdersError("Payment link copy failed. Open the order detail and copy the URL manually.");
    }
  };

  return (
    <main className="restringing-vendors">
      <div className="restringing-vendors__head">
        <div>
          <h1>Restringing Admin</h1>
          <p>Manage restringing vendors, orders, catalog, and staff access.</p>
        </div>
      </div>

      <section className="restringing-card">
        <div className="restringing-section-head">
          <h2>{editingVendorId ? `Edit vendor #${editingVendorId}` : "Create vendor"}</h2>
          <span>{vendors.length} vendors</span>
        </div>
        {vendorError && <div className="restringing-alert restringing-alert--error">{vendorError}</div>}
        {vendorMessage && <div className="restringing-alert restringing-alert--success">{vendorMessage}</div>}
        <form className="restringing-vendor-form" onSubmit={saveVendor}>
          <label>
            Store name
            <input name="name" value={vendorForm.name} onChange={updateVendorForm} placeholder="The Tennis Garage" />
          </label>
          <label>
            Owner name
            <input name="owner_name" value={vendorForm.owner_name} onChange={updateVendorForm} placeholder="Store owner" />
          </label>
          <label>
            Contact email
            <input name="contact_email" type="email" value={vendorForm.contact_email} onChange={updateVendorForm} placeholder="contact@store.com" />
          </label>
          <label>
            Cell
            <input name="cell_phone" value={vendorForm.cell_phone} onChange={updateVendorForm} placeholder="+15551234567" />
          </label>
          <label className="restringing-vendor-form__wide">
            Location
            <Autocomplete
              apiKey={googleApiKey}
              name="address"
              value={vendorForm.address}
              options={{
                types: ["geocode", "establishment"],
                fields: [
                  "name",
                  "address_components",
                  "geometry",
                  "formatted_address",
                ],
              }}
              onPlaceSelected={(place) => {
                if (!place?.geometry?.location) return;
                setVendorForm((current) => ({
                  ...current,
                  address: vendorAddressFromPlace(place),
                  lat: String(place.geometry.location.lat()),
                  lng: String(place.geometry.location.lng()),
                }));
              }}
              onChange={(event) =>
                setVendorForm((current) => ({
                  ...current,
                  address: event.target.value,
                }))
              }
              placeholder="Search vendor address"
            />
          </label>
          <label className="restringing-vendor-form__wide">
            Store description
            <textarea name="description" value={vendorForm.description} onChange={updateVendorForm} placeholder="Describe services, specialties, and store details." />
          </label>
          <label>
            Image
            <input name="image_url" value={vendorForm.image_url} onChange={updateVendorForm} placeholder="https://example.test/store.jpg" />
            <input
              accept="image/jpeg,image/png"
              disabled={vendorImageUploading}
              onChange={uploadVendorImage}
              type="file"
            />
            <span className="restringing-field-note">
              {vendorImageUploading ? "Uploading image..." : "Paste a URL or upload a JPEG/PNG."}
            </span>
          </label>
          <label>
            Website
            <input name="website_url" value={vendorForm.website_url} onChange={updateVendorForm} placeholder="https://store.example.test" />
          </label>
          <label>
            Google business page
            <input name="google_business_url" value={vendorForm.google_business_url} onChange={updateVendorForm} placeholder="https://maps.google.com/..." />
          </label>
          <label className="restringing-vendor-form__wide">
            Collection details
            <textarea name="collection_details" value={vendorForm.collection_details} onChange={updateVendorForm} placeholder="Open 8am-9pm for collection, or please call to confirm collection time." />
            <span className="restringing-field-note">Added to the player notification when the racket is ready for pickup.</span>
          </label>
          <label>
            Latitude
            <input name="lat" type="number" step="0.000001" value={vendorForm.lat} onChange={updateVendorForm} placeholder="12.971599" />
          </label>
          <label>
            Longitude
            <input name="lng" type="number" step="0.000001" value={vendorForm.lng} onChange={updateVendorForm} placeholder="77.594566" />
          </label>
          <label>
            Turnaround days
            <input name="turnaround_days" type="number" min="0" step="1" value={vendorForm.turnaround_days} onChange={updateVendorForm} />
          </label>
          <label>
            Tax rate
            <input name="tax_rate" type="number" min="0" step="0.00001" value={vendorForm.tax_rate} onChange={updateVendorForm} placeholder="0.05" />
          </label>
          <label>
            Platform fee bps
            <input name="platform_fee_bps" type="number" min="0" step="1" value={vendorForm.platform_fee_bps} onChange={updateVendorForm} />
            <span className="restringing-field-note">Applied only when Stripe ID is a connected vendor account.</span>
          </label>
          <label>
            Stripe account
            <input name="stripe_account_id" value={vendorForm.stripe_account_id} onChange={updateVendorForm} placeholder="acct_..." />
            <span className="restringing-field-note">Leave blank or use platform account for the main vendor. Use connected acct_ IDs for marketplace vendors.</span>
          </label>
          <fieldset className="restringing-hours-picker restringing-vendor-form__wide">
            <legend>Hours</legend>
            <div className="restringing-hours-picker__head" aria-hidden="true">
              <span>Day</span>
              <span>Open</span>
              <span>Close</span>
              <span>Closed</span>
            </div>
            {vendorHourDays.map((day) => {
              const row = vendorForm.hoursRows[day.key];
              return (
                <div className="restringing-hours-picker__row" key={day.key}>
                  <span>{day.label}</span>
                  <input
                    aria-label={`${day.label} open time`}
                    disabled={row.closed}
                    type="time"
                    value={row.open}
                    onChange={(event) => updateVendorHours(day.key, "open", event.target.value)}
                  />
                  <input
                    aria-label={`${day.label} close time`}
                    disabled={row.closed}
                    type="time"
                    value={row.close}
                    onChange={(event) => updateVendorHours(day.key, "close", event.target.value)}
                  />
                  <label className="restringing-hours-picker__closed">
                    <input
                      checked={row.closed}
                      type="checkbox"
                      onChange={(event) => updateVendorHours(day.key, "closed", event.target.checked)}
                    />
                    Closed
                  </label>
                </div>
              );
            })}
          </fieldset>
          <label className="restringing-check">
            <input name="active" type="checkbox" checked={vendorForm.active} onChange={updateVendorForm} />
            Active
          </label>
          <div className="restringing-actions">
            <button type="submit" disabled={vendorSaving}>
              {vendorSaving ? "Saving..." : editingVendorId ? "Save vendor" : "Create vendor"}
            </button>
            {editingVendorId && (
              <button type="button" className="restringing-button--secondary" onClick={resetVendorForm}>
                Cancel
              </button>
            )}
          </div>
        </form>

        {vendors.length === 0 ? (
          <p>No vendors found.</p>
        ) : (
          <div className="restringing-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Store</th>
                  <th>Owner</th>
                  <th>Email</th>
                  <th>Cell</th>
                  <th>Location</th>
                  <th>Website</th>
                  <th>Turnaround</th>
                  <th>Stripe account</th>
                  <th>Staff</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {vendors.map((vendor) => {
                  const staffRows = formatVendorStaff(vendor.staff);
                  return (
                    <tr key={vendor.id}>
                      <td>{vendor.name}</td>
                      <td>{vendor.owner_name || "-"}</td>
                      <td>{vendor.contact_email || "-"}</td>
                      <td>{vendor.cell_phone || vendor.public_phone || "-"}</td>
                      <td>{vendor.address || "-"}</td>
                      <td>{vendor.website_url ? <a href={vendor.website_url} target="_blank" rel="noreferrer">Website</a> : "-"}</td>
                      <td>{vendor.turnaround_days || vendor.turnaround_days === 0 ? `${vendor.turnaround_days} days` : "-"}</td>
                      <td>{vendor.stripe_account_id || "-"}</td>
                      <td>
                        {staffRows.length ? (
                          <div className="restringing-staff-list">
                            {staffRows.map((member) => (
                              <div className="restringing-staff-row" key={`${vendor.id}-${member.email}`}>
                                <strong>{member.email}</strong>
                                <span>{member.meta}</span>
                              </div>
                            ))}
                          </div>
                        ) : "-"}
                      </td>
                      <td>{vendor.active ? "Active" : "Inactive"}</td>
                      <td className="restringing-actions">
                        <button type="button" onClick={() => editVendor(vendor.id)}>Edit</button>
                        {vendor.active && (
                          <button type="button" className="restringing-button--secondary" onClick={() => deactivateVendor(vendor.id)}>
                            Deactivate
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="restringing-card">
        <div className="restringing-section-head">
          <h2>All orders</h2>
          <span>{orders.length} shown</span>
        </div>
        <div className="restringing-filters">
          <label>
            Vendor
            <select name="vendor_id" value={filters.vendor_id} onChange={updateFilters}>
              <option value="">All vendors</option>
              {vendors.map((vendor) => (
                <option key={vendor.id} value={vendor.id}>
                  {vendor.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            Fulfillment
            <select name="status" value={filters.status} onChange={updateFilters}>
              <option value="">All statuses</option>
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  {formatStatus(status)}
                </option>
              ))}
            </select>
          </label>

          <label>
            Payment
            <select name="payment_status" value={filters.payment_status} onChange={updateFilters}>
              <option value="">All payments</option>
              {paymentStatusOptions.map((status) => (
                <option key={status} value={status}>
                  {formatStatus(status)}
                </option>
              ))}
            </select>
          </label>
        </div>

        {ordersError && <div className="restringing-alert restringing-alert--error">{ordersError}</div>}
        {ordersLoading ? (
          <p>Loading orders...</p>
        ) : orders.length === 0 ? (
          <p>No restringing orders found.</p>
        ) : (
          <div className="restringing-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Vendor</th>
                  <th>Customer</th>
                  <th>Ordered by</th>
                  <th>Phone</th>
                  <th>Fulfillment</th>
                  <th>Payment</th>
                  <th>Pay link</th>
                  <th>Total</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => {
                  const paymentLink = latestPaymentLink(order);
                  return (
                    <tr key={order.id}>
                      <td>#{order.id}</td>
                      <td>{order.vendor_name || order.vendor_id}</td>
                      <td>{order.customer_full_name || "-"}</td>
                      <td>{coachOrderLabel(order)}</td>
                      <td>{order.customer_phone || "-"}</td>
                      <td>
                        <span className="restringing-pill">{formatStatus(order.fulfillment_status)}</span>
                      </td>
                      <td>{formatStatus(order.payment_status)}</td>
                      <td>
                        {canCopyPaymentLink(order) ? (
                          <button type="button" className="restringing-button--secondary" onClick={() => copyPaymentLink(paymentLink)}>
                            Copy
                          </button>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>{formatMoney(order.total_cents)}</td>
                      <td>{formatDate(order.created_at)}</td>
                      <td>
                        <button type="button" onClick={() => viewOrder(order.id)}>
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {selectedOrderId && (
          <div className="restringing-detail">
            <h3>Order #{selectedOrderId}</h3>
            {detailLoading ? (
              <p>Loading detail...</p>
            ) : selectedOrder?.detail ? (
              <div className="restringing-alert restringing-alert--error">{selectedOrder.detail}</div>
            ) : selectedOrder?.order ? (
              <>
                <div className="restringing-payment-box">
                  <div>
                    <span>Customer checkout link</span>
                    {latestPaymentLink(selectedOrder)?.url ? (
                      <a href={latestPaymentLink(selectedOrder).url} target="_blank" rel="noreferrer">
                        {latestPaymentLink(selectedOrder).url}
                      </a>
                    ) : (
                      <strong>No stored payment link</strong>
                    )}
                  </div>
                  <div>
                    <span>Admin action</span>
                    {canCopyPaymentLink(selectedOrder) ? (
                      <button type="button" onClick={() => copyPaymentLink(latestPaymentLink(selectedOrder))}>
                        Copy link
                      </button>
                    ) : (
                      <strong>No active link</strong>
                    )}
                  </div>
                  <div>
                    <span>Link expires</span>
                    <strong>{formatDate(latestPaymentLink(selectedOrder)?.expires_at)}</strong>
                  </div>
                  <div>
                    <span>Stripe PaymentIntent</span>
                    <strong>{selectedOrder.order.stripe_payment_intent_id || latestPaymentLink(selectedOrder)?.stripe_payment_intent_id || "-"}</strong>
                  </div>
                </div>

                <div className="restringing-detail-grid">
                  <div>
                    <span>Customer</span>
                    <strong>{selectedOrder.order.customer_full_name || "-"}</strong>
                  </div>
                  <div>
                    <span>Email</span>
                    <strong>{selectedOrder.order.customer_email || "-"}</strong>
                  </div>
                  <div>
                    <span>Ordered by</span>
                    <strong>{coachOrderLabel(selectedOrder.order)}</strong>
                  </div>
                  <div>
                    <span>Coach commission</span>
                    <strong>{formatMoney(selectedOrder.order.coach_commission_cents || 0)}</strong>
                  </div>
                  <div>
                    <span>Vendor net</span>
                    <strong>{formatMoney(vendorNetCents(selectedOrder.order))}</strong>
                  </div>
                  <div>
                    <span>Paid</span>
                    <strong>{formatDate(selectedOrder.order.paid_at)}</strong>
                  </div>
                  <div>
                    <span>Fulfilled</span>
                    <strong>{formatDate(selectedOrder.order.fulfilled_at)}</strong>
                  </div>
                </div>

                <h4>Items</h4>
                {(selectedOrder.items || []).map((item) => (
                  <div className="restringing-item" key={item.id}>
                    <div>
                      <span>Racket</span>
                      <strong>{item.racket_make_model || "-"}</strong>
                    </div>
                    <div>
                      <span>String</span>
                      <strong>{formatString(item)}</strong>
                    </div>
                    <div>
                      <span>Gauge</span>
                      <strong>{item.gauge || "-"}</strong>
                    </div>
                    <div>
                      <span>Tension</span>
                      <strong>
                        {item.advice_requested
                          ? "Advice requested"
                          : [item.tension_lbs_mains, item.tension_lbs_crosses].filter(Boolean).join(" / ") || "-"}
                      </strong>
                    </div>
                    {item.notes && <p>{item.notes}</p>}
                  </div>
                ))}
              </>
            ) : null}
          </div>
        )}
      </section>

      <section className="restringing-card">
        <div className="restringing-section-head">
          <h2>Service options</h2>
          <span>{catalogLoading ? "Loading..." : `${serviceTiers.length} tiers`}</span>
        </div>
        {catalogError && <div className="restringing-alert restringing-alert--error">{catalogError}</div>}
        {catalogMessage && <div className="restringing-alert restringing-alert--success">{catalogMessage}</div>}
        <form className="restringing-inline-form" onSubmit={createServiceTier}>
          <label>
            Service name
            <input name="name" value={serviceTierForm.name} onChange={updateServiceTierForm} />
          </label>
          <label>
            Price ($)
            <input name="price" type="number" step="0.01" min="0" value={serviceTierForm.price} onChange={updateServiceTierForm} />
          </label>
          <label>
            Composition
            <select name="string_composition" value={serviceTierForm.string_composition} onChange={updateServiceTierForm}>
              <option value="">{serviceCompositionLabel(null)}</option>
              {serviceCompositions.map((composition) => (
                <option key={composition} value={composition}>{serviceCompositionLabel(composition)}</option>
              ))}
            </select>
          </label>
          {!serviceTierForm.string_composition && (
            <label>
              String category
              <select name="string_category" value={serviceTierForm.string_category} onChange={updateServiceTierForm}>
                <option value="">Own string</option>
                {stringCategories.map((category) => (
                  <option key={category} value={category}>{formatStatus(category)}</option>
                ))}
              </select>
            </label>
          )}
          <label className="restringing-check">
            <input name="active" type="checkbox" checked={serviceTierForm.active} onChange={updateServiceTierForm} /> Active
          </label>
          <button type="submit">Add service option</button>
        </form>
        <div className="restringing-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Service</th>
                <th>Price</th>
                <th>Composition</th>
                <th>String category</th>
                <th>Active</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {serviceTiers.map((tier) => {
                const edit = serviceTierEdits[tier.id] || {};
                return (
                  <tr key={tier.id}>
                    <td>
                      <input
                        value={edit.name || ""}
                        onChange={(event) => updateTierEdit(tier.id, "name", event.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={edit.price || ""}
                        onChange={(event) => updateTierEdit(tier.id, "price", event.target.value)}
                      />
                    </td>
                    <td>
                      <select
                        value={edit.string_composition || ""}
                        onChange={(event) => updateTierEdit(tier.id, "string_composition", event.target.value)}
                      >
                        <option value="">{serviceCompositionLabel(null)}</option>
                        {serviceCompositions.map((composition) => (
                          <option key={composition} value={composition}>{serviceCompositionLabel(composition)}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      {!edit.string_composition && (
                        <select
                          value={edit.string_category || ""}
                          onChange={(event) => updateTierEdit(tier.id, "string_category", event.target.value)}
                        >
                          <option value="">Own string</option>
                          {stringCategories.map((category) => (
                            <option key={category} value={category}>{formatStatus(category)}</option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={edit.active !== false}
                        onChange={(event) => updateTierEdit(tier.id, "active", event.target.checked)}
                      />
                    </td>
                    <td>
                      <button type="button" onClick={() => saveTier(tier.id)}>Save</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="restringing-card">
        <div className="restringing-section-head">
          <h2>String options</h2>
          <span>{catalogLoading ? "Loading..." : `${strings.length} strings`}</span>
        </div>
        <form className="restringing-inline-form" onSubmit={createString}>
          <input name="brand" value={stringForm.brand} onChange={updateStringForm} placeholder="Brand" />
          <input name="name" value={stringForm.name} onChange={updateStringForm} placeholder="Name" />
          <select name="category" value={stringForm.category} onChange={updateStringForm}>
            {stringCategories.map((category) => (
              <option key={category} value={category}>{formatStatus(category)}</option>
            ))}
          </select>
          <div className="restringing-gauge-picker">
            {stringGaugeOptions.map((gauge) => (
              <label key={gauge} className="restringing-gauge-chip">
                <input
                  type="checkbox"
                  checked={normalizeGauges(stringForm.gauges).includes(gauge)}
                  onChange={() => toggleStringFormGauge(gauge)}
                />
                {gauge}
              </label>
            ))}
          </div>
          <label className="restringing-check">
            <input name="active" type="checkbox" checked={stringForm.active} onChange={updateStringForm} />
            Active
          </label>
          <button type="submit">Add string</button>
        </form>
        <div className="restringing-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Brand</th>
                <th>Name</th>
                <th>Category</th>
                <th>Gauge</th>
                <th>Active</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {strings.map((string) => {
                const edit = stringEdits[string.id] || {};
                return (
                  <tr key={string.id}>
                    <td>
                      <input
                        value={edit.brand || ""}
                        onChange={(event) => updateStringEdit(string.id, "brand", event.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        value={edit.name || ""}
                        onChange={(event) => updateStringEdit(string.id, "name", event.target.value)}
                      />
                    </td>
                    <td>
                      <select
                        value={edit.category || "syn_gut"}
                        onChange={(event) => updateStringEdit(string.id, "category", event.target.value)}
                      >
                        {stringCategories.map((category) => (
                          <option key={category} value={category}>{formatStatus(category)}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <div
                        className="restringing-gauge-picker restringing-gauge-picker--table"
                      >
                        {stringGaugeOptions.map((gauge) => (
                          <label key={gauge} className="restringing-gauge-chip">
                            <input
                              type="checkbox"
                              checked={normalizeGauges(edit.gauges).includes(gauge)}
                              onChange={() => toggleStringEditGauge(string.id, gauge)}
                            />
                            {gauge}
                          </label>
                        ))}
                      </div>
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={edit.active !== false}
                        onChange={(event) => updateStringEdit(string.id, "active", event.target.checked)}
                      />
                    </td>
                    <td>
                      <button type="button" onClick={() => saveString(string.id)}>Save</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="restringing-card">
        <div className="restringing-section-head">
          <h2>Recommendation service</h2>
          <span>{catalogLoading ? "Loading..." : `${recommendationCategories.length} categories`}</span>
        </div>
        <form className="restringing-inline-form restringing-inline-form--recommendation" onSubmit={createRecommendation}>
          <select name="category" value={recommendationForm.category} onChange={updateRecommendationForm}>
            {stringCategories.map((category) => (
              <option key={category} value={category}>{formatStatus(category)}</option>
            ))}
          </select>
          <input name="category_label" value={recommendationForm.category_label} onChange={updateRecommendationForm} placeholder="Label" />
          <input name="default_tension_lbs" type="number" step="0.5" value={recommendationForm.default_tension_lbs} onChange={updateRecommendationForm} placeholder="Default lbs" />
          <input name="tension_min_lbs" type="number" step="0.5" value={recommendationForm.tension_min_lbs} onChange={updateRecommendationForm} placeholder="Min lbs" />
          <input name="tension_max_lbs" type="number" step="0.5" value={recommendationForm.tension_max_lbs} onChange={updateRecommendationForm} placeholder="Max lbs" />
          <label className="restringing-check">
            <input name="active" type="checkbox" checked={recommendationForm.active} onChange={updateRecommendationForm} />
            Active
          </label>
          <button type="submit">Save config</button>
        </form>
        <div className="restringing-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Category</th>
                <th>Label</th>
                <th>Default</th>
                <th>Min</th>
                <th>Max</th>
                <th>Active</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {recommendationCategories.map((item) => {
                const edit = recommendationEdits[item.category] || {};
                return (
                  <tr key={item.category}>
                    <td>{formatStatus(item.category)}</td>
                    <td>
                      <input
                        value={edit.category_label || ""}
                        onChange={(event) => updateRecommendationEdit(item.category, "category_label", event.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        step="0.5"
                        value={edit.default_tension_lbs || ""}
                        onChange={(event) => updateRecommendationEdit(item.category, "default_tension_lbs", event.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        step="0.5"
                        value={edit.tension_min_lbs || ""}
                        onChange={(event) => updateRecommendationEdit(item.category, "tension_min_lbs", event.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        step="0.5"
                        value={edit.tension_max_lbs || ""}
                        onChange={(event) => updateRecommendationEdit(item.category, "tension_max_lbs", event.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={edit.active !== false}
                        onChange={(event) => updateRecommendationEdit(item.category, "active", event.target.checked)}
                      />
                    </td>
                    <td className="restringing-actions">
                      <button type="button" onClick={() => saveRecommendation(item.category)}>Save</button>
                      <button type="button" className="restringing-button--secondary" onClick={() => disableRecommendation(item.category)}>Disable</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="restringing-card">
        <h2>Create vendor staff</h2>
        <form onSubmit={submit}>
          <label>
            Vendor
            <select name="vendorId" value={form.vendorId} onChange={updateForm}>
              <option value="">Select vendor</option>
              {vendors.map((vendor) => (
                <option key={vendor.id} value={vendor.id}>
                  {vendor.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            Email
            <input
              name="email"
              type="email"
              value={form.email}
              onChange={updateForm}
              placeholder="vendor@test.com"
            />
          </label>

          <label>
            Password
            <input
              name="password"
              type="password"
              value={form.password}
              onChange={updateForm}
              placeholder="Temporary password"
            />
          </label>

          <label>
            Role
            <input name="role" value={form.role} onChange={updateForm} placeholder="owner" />
          </label>

          {error && <div className="restringing-alert restringing-alert--error">{error}</div>}
          {message && <div className="restringing-alert restringing-alert--success">{message}</div>}

          <button type="submit" disabled={loading}>
            {loading ? "Creating..." : "Create and assign vendor user"}
          </button>
        </form>
      </section>

    </main>
  );
}

export default RestringingVendors;
